import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import BeforeAfterSlider from '../components/ui/BeforeAfterSlider';
import { LuminoIcon, ChromoIcon, SpectroIcon, PietImaging } from '../components/icons/XionIcons';
import { distributor } from '../data/mock';
import { useMotion } from '../context/MotionContext';
import { trackCTAClick } from '../utils/analytics';
import GlitchText from '../components/ui/GlitchText';

const PIET_MODES = [
  {
    id: 'lumino',
    icon: LuminoIcon,
    name: 'PIET lumino',
    before: '/products/piet/piet-lumino-without.jpg',
    after: '/products/piet/piet-lumino-with.jpg',
    what: 'Both bright and dark areas of the image are enhanced so they are equally well represented.',
    benefit: 'Better visualisation of detail in scenes with extreme lighting differences — fewer wash-outs and fewer dark, hidden regions.'
  },
  {
    id: 'chromo',
    icon: ChromoIcon,
    name: 'PIET chromo',
    before: '/products/piet/piet-chromo-without.jpg',
    after: '/products/piet/piet-chromo-with.jpg',
    what: 'Details are highlighted and the colour contrast is intensified.',
    benefit: 'Improved ability to distinguish tissue types and identify subtle colour variations that may indicate pathology.'
  },
  {
    id: 'spectro',
    icon: SpectroIcon,
    name: 'PIET spectro',
    before: '/products/piet/piet-spectro-without.jpg',
    after: '/products/piet/piet-spectro-with.jpg',
    what: 'By shifting the colour spectrum, tissue structures are displayed in a more differentiated manner.',
    benefit: 'Enhanced differentiation of tissue structures for a more nuanced read of tissue characteristics and potential abnormalities.'
  }
];

const PIET_AVAILABILITY = ['HD', '4K / UHD', '2D Endoscopy', '3D Endoscopy'];

const PIET_PRODUCTS = [
  { name: 'EndoSTROBE Spectar', desc: 'Laryngo-stroboscopy & phoniatrics workstation', link: '/system-solutions/endostrobe-spectar' },
  { name: 'EndoSURGERY 3D Spectar', desc: 'True 3D surgical visualisation platform', link: '/system-solutions/endosurgery-3d-spectar' },
  { name: 'MATRIX P / PL Spectar Processors', desc: 'Surgery camera processors up to 4K/UHD', link: '/products/camera-processors-for-surgery' },
  { name: 'EndoSTROBE PL / EL Spectar Processors', desc: 'Video-stroboscopy camera processors', link: '/products/camera-processors-for-laryngo-stroboscopy' }
];

const PietTechnologyPage = () => {
  const { prefersReducedMotion } = useMotion();

  return (
    <>
      <SEOHead
        title="PIET — Professional Image Enhancement Technology"
        description="XION PIET (Professional Image Enhancement Technology) adds three situational visualisation modes — PIET lumino, chromo and spectro — across HD, 4K, 2D and 3D endoscopy for advanced diagnostic clarity."
        path="/technology/piet"
      />
      <Header />
      <main className="pt-32 min-h-screen bg-black grid-bg" data-testid="piet-technology-page">
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Hero */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12 max-w-4xl"
          >
            <p className="terminal-label text-[#00FFD1] mb-4">// XION Technology</p>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              {prefersReducedMotion ? 'PIET' : <GlitchText text="PIET" />}
            </h1>
            <p className="text-xl md:text-2xl text-[#00FFD1] font-semibold mb-6">
              Advanced diagnostic options with PIET
            </p>
            <p className="text-white/70 text-lg leading-relaxed">
              XION&rsquo;s <span className="text-white font-medium">Professional Image Enhancement Technology (PIET)</span> extends
              the imaging system by adding three situational visualisation technologies. The modes
              <span className="text-white font-medium"> PIET lumino</span>,
              <span className="text-white font-medium"> PIET chromo</span> and
              <span className="text-white font-medium"> PIET spectro</span> are available in all resolutions
              (HD, 4K) as well as in 2D and 3D endoscopy.
            </p>
            <div className="flex flex-wrap gap-3 mt-7" data-testid="piet-availability">
              {PIET_AVAILABILITY.map((a) => (
                <span key={a} className="px-4 py-1.5 bg-[#00FFD1]/5 border border-[#00FFD1]/25 rounded-full text-white/80 text-xs font-mono">
                  {a}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Modes */}
          <section className="mb-16">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="mb-8 flex items-center gap-3"
            >
              <PietImaging className="text-[#00FFD1]" size={22} />
              <h2 className="text-3xl font-bold text-white">Three Situational Modes</h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {PIET_MODES.map((mode, index) => {
                const Icon = mode.icon;
                return (
                  <motion.div
                    key={mode.id}
                    initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="card-dark p-0 flex flex-col overflow-hidden"
                    data-testid={`piet-mode-${mode.id}`}
                  >
                    {mode.before && mode.after && (
                      <div className="relative border-b border-white/10">
                        <BeforeAfterSlider
                          beforeSrc={mode.before}
                          afterSrc={mode.after}
                          beforeLabel="Without PIET"
                          afterLabel="With PIET"
                          alt={mode.name}
                          testId={`piet-slider-${mode.id}`}
                        />
                        <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] font-mono text-white/85 bg-black/70 px-2 py-1 rounded pointer-events-none flex items-center gap-1">
                          Drag to compare
                        </span>
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="w-14 h-14 border border-[#00FFD1]/30 flex items-center justify-center mb-4">
                        <Icon className="text-[#00FFD1]" size={28} />
                      </div>
                      <h3 className="text-xl font-semibold text-[#00FFD1] font-mono mb-3">{mode.name}</h3>
                      <p className="text-white/80 text-sm leading-relaxed mb-4">{mode.what}</p>
                      <div className="mt-auto pt-4 border-t border-white/10">
                        <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Clinical benefit</p>
                        <p className="text-white/70 text-sm leading-relaxed">{mode.benefit}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Watch — XION imaging in action */}
          <section className="mb-16">
            <p className="terminal-label text-[#00FFD1] mb-3">// Watch</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">See XION imaging in action</h2>
            <div
              className="relative w-full rounded-lg overflow-hidden border border-white/10 bg-black"
              style={{ aspectRatio: '16 / 9' }}
              data-testid="piet-youtube-embed"
            >
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube-nocookie.com/embed/kgeTCL6z6lY"
                title="XION Medical — imaging demonstration"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </section>


          {/* Where PIET is available */}
          <section className="mb-16">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="mb-8"
            >
              <h2 className="text-3xl font-bold text-white mb-2">Where you&rsquo;ll find PIET</h2>
              <p className="text-white/60">PIET is built into the Spectar camera platform across these XION systems and processors.</p>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-5">
              {PIET_PRODUCTS.map((p, index) => (
                <motion.div
                  key={p.name}
                  initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                >
                  <Link
                    to={p.link}
                    className="card-dark p-6 flex items-center justify-between gap-4 group hover:border-[#00FFD1]/40 transition-colors"
                    data-testid={`piet-product-${index}`}
                  >
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-[#00FFD1] transition-colors">{p.name}</h3>
                      <p className="text-white/55 text-sm">{p.desc}</p>
                    </div>
                    <ArrowRight size={18} className="text-[#00FFD1] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Peer-reviewed evidence callout */}
          <Link
            to="/clinical-evidence"
            data-testid="piet-evidence-link"
            className="group flex flex-col sm:flex-row sm:items-center gap-4 bg-[#00FFD1]/[0.06] border border-[#00FFD1]/30 rounded-lg p-6 mb-12 hover:border-[#00FFD1]/60 transition-colors"
          >
            <div className="flex-1">
              <p className="terminal-label text-[#00FFD1] mb-1">// Peer-reviewed</p>
              <p className="text-white font-semibold">
                Independent study: PIET matches NBI for endoscopic swallowing diagnostics (FEES)
              </p>
              <p className="text-white/55 text-sm mt-1">
                Niessen et al., <span className="italic">European Archives of Oto-Rhino-Laryngology</span>, 2025 — see the clinical evidence.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[#00FFD1] text-sm font-mono whitespace-nowrap group-hover:underline">
              View evidence <ArrowRight size={15} />
            </span>
          </Link>


          {/* CTA */}
          <motion.section
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#0a0a0a] border border-[#00FFD1]/25 p-8 md:p-12"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="terminal-label text-[#00FFD1] mb-3">// See PIET in action</p>
                <h3 className="text-2xl font-bold text-white mb-4">Request a PIET-enabled demonstration</h3>
                <p className="text-white/60">
                  Arrange a clinical demonstration of PIET on the Spectar platform in the Philippines through
                  our exclusive distributor, <span className="text-[#00FFD1] font-medium">{distributor.name}</span>.
                </p>
              </div>
              <div className="flex flex-col gap-4">
                <Link to="/contact-ph" className="btn-primary w-full justify-center" data-testid="piet-request-quote">
                  Request Quotation (PH)
                  <ArrowRight size={16} />
                </Link>
                <a
                  href={distributor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackCTAClick('fms', 'PietTechnologyPage')}
                  className="btn-secondary w-full justify-center"
                >
                  Contact FMSInc.
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </motion.section>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default PietTechnologyPage;
